/**
 * The booking-form vocabulary — the short code each document prints under, its
 * name, and its tag colour.
 *
 * The PWA talks to the agent server in short CODES (the create screen posts
 * `form_type: 'ALS'`, the server maps it to the enum pair), so this file is the
 * code side of cid-agent-server/src/utils/bookingFormTypes.js. Six screens each
 * had their own label map and their own `code === 'ALS' || code === 'ALR'` test;
 * adding the exclusive letters meant finding every one, and a missed test does
 * not crash — it quietly drops ES/EL out of whatever it guarded.
 *
 * The six documents:
 *
 *   OTP  Agreement to Purchase              SALES
 *   OTR  Agreement to Rent                  RENT
 *   ALS  Authorisation to Sell              SALES
 *   ALR  Authorisation to Let               RENT
 *   ES   Exclusive Authorisation to Sell    SALES
 *   EL   Exclusive Authorisation to Let     RENT
 *
 * ALS/ALR and ES/EL behave identically downstream — each appoints CID, backs a
 * listing, then becomes an OTP/OTR. They differ in the document they print and
 * the fee they earn, nothing else.
 */

export const FORM_CODES = ['OTP', 'OTR', 'ALS', 'ALR', 'ES', 'EL'];

/** Codes that appoint CID, and can therefore back a listing. */
export const AUTH_CODES = ['ALS', 'ALR', 'ES', 'EL'];

/** The exclusive pair — the only codes that read differently. */
export const EXCLUSIVE_CODES = ['ES', 'EL'];

/** Codes quoted off a selling price rather than a monthly rental. */
export const SALES_CODES = ['OTP', 'ALS', 'ES'];

export const CODE_LABELS = {
  OTP: 'Offer to Purchase',
  OTR: 'Offer to Rent',
  ALS: 'Authorisation to Sell',
  ALR: 'Authorisation to Let',
  ES: 'Exclusive Authorisation to Sell',
  EL: 'Exclusive Authorisation to Let',
};

/** Sentence-case titles for the detail screens' headers. */
export const CODE_TITLE = {
  OTP: 'Agreement to purchase',
  OTR: 'Agreement to rent',
  ALS: 'Authorisation to sell',
  ALR: 'Authorisation to let',
  ES: 'Exclusive authorisation to sell',
  EL: 'Exclusive authorisation to let',
};

/**
 * Chip colours. The exclusive pair sits beside its general counterpart on the
 * warm side of the wheel — an ES is a stronger ALS, not an unrelated document.
 */
export const CODE_COLOR = {
  OTP: 'blue', OTR: 'cyan', ALS: 'green', ALR: 'purple', ES: 'gold', EL: 'magenta',
};

/** The create screen's code picker. */
export const CODE_OPTS = FORM_CODES.map((c) => ({ label: c, value: c }));

/** The list screen's type filter, with its "all" row. */
export const CODE_FILTER_OPTS = [
  { value: '', label: 'All types' },
  ...FORM_CODES.map((c) => ({ value: c, label: `${c} · ${CODE_LABELS[c]}` })),
];

export const isAuthCode = (code) => AUTH_CODES.includes(code);
export const isExclusiveCode = (code) => EXCLUSIVE_CODES.includes(code);
export const isSalesCode = (code) => SALES_CODES.includes(code);

/**
 * ALR / EL: whether the asking rental already carries SST — `form_data.rental_sst_basis`.
 * EXCLUSIVE prints "exclusive of SST … The relevant SST rate will be charged when
 * applicable" and needs no rate; INCLUSIVE prints "inclusive of X% SST" off
 * `tax_percent`. A letter saved before the choice existed has no value and printed
 * the inclusive wording, so a missing value reads as INCLUSIVE everywhere.
 */
export const RENTAL_SST_EXCLUSIVE = 'EXCLUSIVE';
export const RENTAL_SST_INCLUSIVE = 'INCLUSIVE';
export const RENTAL_SST_OPTIONS = [
  { value: RENTAL_SST_EXCLUSIVE, label: 'Exclusive of SST' },
  { value: RENTAL_SST_INCLUSIVE, label: 'Inclusive of SST' },
];

/** How a saved letter states the rental's SST — "Inclusive of 8% SST". */
export function rentalSstText(formData) {
  const fd = formData || {};
  if (fd.rental_sst_basis === RENTAL_SST_EXCLUSIVE) return 'Exclusive of SST (charged when applicable)';
  return fd.tax_percent != null && fd.tax_percent !== '' ? `Inclusive of ${fd.tax_percent}% SST` : 'Inclusive of SST';
}
